import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import SlidePanel from "../../../components/SlidePanel/SlidePanel";
import * as hrmsService from "../../../services/hrmsService";
import { HR_PERMISSIONS, hasHrPermission } from "../../../constants/hrPermissions";
import { FormDrawer } from "../hrForm";
import { LoadState, PageHeader, PrimaryButton, SecondaryButton } from "../hrComponents";
import { useHr } from "../HrGate";
import { badgeStyle, cardStyle, formatDate, formatRange, pageStyle, tdStyle, thStyle, useLoad } from "../hrUi";
import { CURRENCY, RUN_STEPS, RUN_TYPE_LABEL, exceptionLabel, formatMoney, registerSheet, runActions, runStatusBadge, runStatusLabel } from "./payrollHelpers";

// PAY-05 / PAY-06: one payroll run. Calculate, review what needs attention, send for approval, lock, publish.
const LEVEL = { ERROR: ["#FEE2E2", "#991B1B"], WARNING: ["#FEF3C7", "#92400E"] };
const Chip = ({ level, children }) => <span style={badgeStyle(...(LEVEL[level] || LEVEL.WARNING))}>{children}</span>;

function Stat({ label, value, tone }) {
  return (
    <div style={{ ...cardStyle, padding: "12px 16px", flex: "1 1 150px" }}>
      <div style={{ fontSize: 12, color: "#6B7280" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: tone || "#111827" }}>{value}</div>
    </div>
  );
}

function Steps({ status }) {
  const at = RUN_STEPS.indexOf(status);
  return (
    <div className="d-flex flex-wrap gap-2 mb-3" aria-label="Progress">
      {RUN_STEPS.map((s, i) => (
        <span key={s} style={{ fontSize: 12, fontWeight: i === at ? 700 : 500, color: i <= at ? "#065F46" : "#9CA3AF", background: i === at ? "#D1FAE5" : "transparent", padding: "2px 10px", borderRadius: 12 }}>
          {i + 1}. {runStatusLabel(s)}
        </span>
      ))}
    </div>
  );
}

function EmployeeDrawer({ runId, employeeId, run, canManage, onHide, onChanged, currency }) {
  const { data, loading, error, reload } = useLoad(() => hrmsService.getRunEmployee(runId, employeeId), [runId, employeeId], { enabled: !!employeeId });
  const [holding, setHolding] = useState(false);
  const lines = data?.lines || [];
  const part = (kind) => lines.filter((l) => l.kind === kind);
  const editable = ["DRAFT", "CALCULATED", "LOCKED", "PUBLISHED"].includes(run.status);
  const table = (title, rows) =>
    rows.length ? (
      <div className="mb-3">
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{title}</div>
        <table className="table mb-0" style={{ fontSize: 13 }}>
          <tbody>
            {rows.map((l, i) => (
              <tr key={i}>
                <td style={tdStyle}>
                  {l.component_name}
                  {l.note ? <div style={{ fontSize: 11, color: "#6B7280" }}>{l.note}</div> : null}
                </td>
                <td style={{ ...tdStyle, textAlign: "right" }}>{formatMoney(l.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : null;
  const release = async () => {
    const res = await hrmsService.releasePayment(runId, employeeId);
    if (res?.success) {
      toast.success(res.message || "Released");
      reload();
      onChanged();
    } else toast.error(hrmsService.responseMessage(res));
  };
  return (
    <SlidePanel show={!!employeeId} onHide={onHide} title={data ? data.full_name : "Employee"} width={520}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {data ? (
          <>
            <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 10 }}>
              {data.employee_code} · {data.paid_days} of {data.days_basis_value} days paid{Number(data.lop_days) ? ` · ${data.lop_days} loss of pay` : ""}
            </div>
            {data.exceptions.length ? (
              <div className="mb-3">
                {data.exceptions.map((x, i) => (
                  <div key={i} className="mb-1" style={{ fontSize: 13 }}>
                    <Chip level={x.level}>{exceptionLabel(x.code)}</Chip> <span style={{ color: "#374151" }}>{x.message}</span>
                  </div>
                ))}
              </div>
            ) : null}
            {data.is_held ? (
              <div className="mb-3" style={{ background: "#FEF3C7", borderRadius: 8, padding: "8px 12px", fontSize: 13 }}>
                Payment held: {data.hold_reason}
              </div>
            ) : null}
            {table(`Earnings (${currency})`, part("EARNING"))}
            {table("Deductions", part("DEDUCTION"))}
            {table("Reimbursements", part("REIMBURSEMENT"))}
            {table("Employer cost (not on the payslip)", part("EMPLOYER"))}
            <div className="d-flex justify-content-between mb-3" style={{ background: "#F1F4F8", borderRadius: 8, padding: "12px 14px", fontWeight: 700 }}>
              <span>Net pay</span>
              <span>{formatMoney(data.net)}</span>
            </div>
            {data.snapshot?.lop_dates?.length ? <div style={{ fontSize: 12, color: "#6B7280" }}>Loss of pay on: {data.snapshot.lop_dates.map(formatDate).join(", ")}</div> : null}
            {canManage && editable && data.status === "INCLUDED" ? (
              <div className="mt-3">
                {data.is_held ? <SecondaryButton onClick={release}>Release payment</SecondaryButton> : <SecondaryButton onClick={() => setHolding(true)}>Hold payment</SecondaryButton>}
              </div>
            ) : null}
          </>
        ) : null}
      </LoadState>
      <FormDrawer
        show={holding}
        title="Hold payment"
        submitLabel="Hold this payment"
        fields={[{ key: "reason", label: "Why is the payment held?", required: true, max: 200 }]}
        onHide={() => setHolding(false)}
        onSave={(clean) => hrmsService.holdPayment(runId, employeeId, clean.reason)}
        onDone={() => {
          reload();
          onChanged();
        }}
      />
    </SlidePanel>
  );
}

export default function PayrollRunDetail() {
  const { id } = useParams();
  const { access } = useHr();
  const canManage = hasHrPermission(access, HR_PERMISSIONS.PAYROLL_ADMIN);
  const canApprove = hasHrPermission(access, HR_PERMISSIONS.FINANCE_APPROVER);
  const settings = useLoad(() => hrmsService.getSettings(), []);
  const run = useLoad(() => hrmsService.getPayrollRun(id), [id]);
  const [only, setOnly] = useState("all");
  const people = useLoad(() => hrmsService.listRunEmployees(id, { only: only === "all" ? undefined : only, limit: 500 }), [id, only, run.data?.calculated_at]);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [fnfEditing, setFnfEditing] = useState(false);

  const r = run.data;
  const currency = CURRENCY[settings.data?.country_pack] || "";
  const refresh = () => {
    run.reload();
    people.reload();
  };

  const perform = async (call, { confirm, done }) => {
    if (confirm && !window.confirm(confirm)) return;
    setBusy(true);
    try {
      const res = await call();
      if (res?.success) {
        toast.success(done || res.message || "Done");
        refresh();
      } else toast.error(hrmsService.responseMessage(res));
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const ACTION = {
    calculate: { label: r?.status === "DRAFT" ? "Calculate" : "Calculate again", primary: r?.status === "DRAFT", go: () => perform(() => hrmsService.calculatePayrollRun(id), { done: "Calculated" }) },
    submit: { label: "Send for approval", primary: true, go: () => perform(() => hrmsService.submitPayrollRun(id), { done: "Sent for approval" }) },
    approve: { label: "Approve", primary: true, go: () => perform(() => hrmsService.approvePayrollRun(id), { done: "Approved" }) },
    reject: { label: "Send back", go: () => setRejecting(true) },
    withdraw: { label: "Take back to correct", go: () => perform(() => hrmsService.withdrawPayrollRun(id), { done: "Taken back" }) },
    lock: { label: "Lock", primary: true, go: () => perform(() => hrmsService.lockPayrollRun(id), { confirm: "Lock this run? The figures become final and cannot be changed.", done: "Run locked" }) },
    publish: { label: "Publish payslips", primary: true, go: () => perform(() => hrmsService.publishPayrollRun(id), { confirm: "Publish? Everyone who is not held gets their payslip now.", done: "Payslips published" }) },
    cancel: { label: "Cancel run", go: () => setCancelling(true) },
  };

  const exportRegister = async () => {
    const res = await hrmsService.getRunRegister(id);
    if (!res?.success) return toast.error(hrmsService.responseMessage(res));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(registerSheet(res.data)), "Payroll register");
    XLSX.writeFile(wb, `payroll-register-${r.period_start.slice(0, 7)}.xlsx`);
  };
  const exportPension = async () => {
    const res = await hrmsService.getRunContributions(id);
    if (!res?.success) return toast.error(hrmsService.responseMessage(res));
    const rows = res.data.rows;
    if (!rows.length) return toast.info("Nobody in this run pays a pension contribution");
    const sheet = [["Code", "Name", "Nationality", "Contribution salary", "Employee share", "Employer share", "Total"], ...rows.map((p) => [p.employee_code, p.full_name, p.nationality, p.contribution_salary, p.employee_share, p.employer_share, p.total])];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sheet), "Pension");
    XLSX.writeFile(wb, `pension-${r.period_start.slice(0, 7)}.xlsx`);
  };

  const t = r?.totals;
  const codes = Object.entries(t?.by_code || {});

  return (
    <div style={pageStyle}>
      <div className="mb-2" style={{ fontSize: 13 }}>
        <Link to="/hr/payroll">← Payroll</Link>
      </div>
      <LoadState loading={run.loading} error={run.error} onRetry={run.reload} rows={6}>
        {r ? (
          <>
            <PageHeader
              title={r.title}
              subtitle={`${RUN_TYPE_LABEL[r.run_type] || r.run_type} · ${formatRange(r.period_start, r.period_end)}${r.pay_date ? ` · pay date ${formatDate(r.pay_date)}` : ""}`}
              actions={
                <>
                  <span style={{ ...runStatusBadge(r.status), alignSelf: "center" }}>{runStatusLabel(r.status)}</span>
                  {runActions(r, { canManage, canApprove }).map((a) =>
                    ACTION[a].primary ? (
                      <PrimaryButton key={a} onClick={ACTION[a].go} disabled={busy}>
                        {ACTION[a].label}
                      </PrimaryButton>
                    ) : (
                      <SecondaryButton key={a} onClick={ACTION[a].go} disabled={busy}>
                        {ACTION[a].label}
                      </SecondaryButton>
                    )
                  )}
                </>
              }
            />
            <Steps status={r.status} />
            {r.reject_reason && r.status === "CALCULATED" ? (
              <div className="mb-3" style={{ background: "#FEF3C7", borderRadius: 8, padding: "8px 12px", fontSize: 13 }} role="status">
                Sent back: {r.reject_reason}
              </div>
            ) : null}

            {r.run_type === "FNF" ? (
              <div style={{ ...cardStyle, padding: "12px 16px", marginBottom: 16 }}>
                <div className="d-flex justify-content-between align-items-center">
                  <strong style={{ fontSize: 14 }}>Settlement details</strong>
                  {canManage && ["DRAFT", "CALCULATED"].includes(r.status) ? (
                    <button type="button" className="btn btn-link btn-sm" onClick={() => setFnfEditing(true)}>
                      Change
                    </button>
                  ) : null}
                </div>
                <div style={{ fontSize: 13, color: "#374151" }}>
                  Unused leave paid: {r.fnf_params?.leave_encashment_days || 0} days · Notice pay: {r.fnf_params?.notice_pay_days || 0} days · Notice recovery: {r.fnf_params?.notice_shortfall_days || 0} days
                  {r.separation ? ` · notice period ${r.separation.notice_period_days} days, last working day ${formatDate(r.separation.last_working_day)}` : ""}
                </div>
              </div>
            ) : null}

            {t && r.calculated_at ? (
              <>
                <div className="d-flex flex-wrap gap-3 mb-3">
                  <Stat label="People paid" value={t.employees - t.held} />
                  <Stat label={`Gross (${currency})`} value={formatMoney(t.gross)} />
                  <Stat label="Deductions" value={formatMoney(t.deductions)} />
                  <Stat label="Net payable" value={formatMoney(t.payable_net)} tone="#065F46" />
                  <Stat label="Employer cost" value={formatMoney(t.employer_cost)} />
                  {t.held ? <Stat label="Held" value={t.held} tone="#92400E" /> : null}
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                  <SecondaryButton onClick={() => setOnly("all")} aria-pressed={only === "all"}>
                    Everyone
                  </SecondaryButton>
                  <SecondaryButton onClick={() => setOnly("exceptions")} aria-pressed={only === "exceptions"}>
                    Needs attention ({t.errors + t.warnings})
                  </SecondaryButton>
                  {t.held ? (
                    <SecondaryButton onClick={() => setOnly("held")} aria-pressed={only === "held"}>
                      Held ({t.held})
                    </SecondaryButton>
                  ) : null}
                  <span style={{ flex: 1 }} />
                  <SecondaryButton onClick={exportRegister}>Register (Excel)</SecondaryButton>
                  <SecondaryButton onClick={exportPension}>Pension (Excel)</SecondaryButton>
                </div>
                {codes.length ? (
                  <div className="d-flex flex-wrap gap-2 mb-3" aria-label="What needs attention">
                    {codes.map(([code, n]) => (
                      <span key={code} style={badgeStyle("#F3F4F6", "#374151")}>
                        {exceptionLabel(code)}: {n}
                      </span>
                    ))}
                  </div>
                ) : null}
                {t.blocking ? (
                  <div className="mb-3" style={{ background: "#FEE2E2", borderRadius: 8, padding: "8px 12px", fontSize: 13 }} role="alert">
                    {t.blocking} {t.blocking === 1 ? "person has" : "people have"} an error. Fix it and calculate again, or hold their payment, before sending for approval.
                  </div>
                ) : null}

                <LoadState loading={people.loading} error={people.error} onRetry={people.reload}>
                  <div style={{ ...cardStyle, overflowX: "auto" }}>
                    <table className="table mb-0" style={{ minWidth: 760 }}>
                      <thead>
                        <tr>
                          {["Employee", "Days", "Gross", "Deductions", "Net", "Notes"].map((h, i) => (
                            <th key={h} style={{ ...thStyle, textAlign: i >= 2 && i <= 4 ? "right" : "left" }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(people.data || []).map((p) => (
                          <tr key={p.employee_id} onClick={() => setViewing(p.employee_id)} style={{ cursor: "pointer", opacity: p.status === "EXCLUDED" ? 0.7 : 1 }}>
                            <td style={tdStyle}>
                              {p.full_name}
                              <div style={{ fontSize: 12, color: "#6B7280" }}>{p.employee_code}</div>
                            </td>
                            <td style={tdStyle}>
                              {p.paid_days}/{p.days_basis_value}
                              {Number(p.lop_days) ? <div style={{ fontSize: 12, color: "#991B1B" }}>{p.lop_days} LOP</div> : null}
                            </td>
                            <td style={{ ...tdStyle, textAlign: "right" }}>{formatMoney(p.gross)}</td>
                            <td style={{ ...tdStyle, textAlign: "right" }}>{formatMoney(p.deductions)}</td>
                            <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>{formatMoney(p.net)}</td>
                            <td style={tdStyle}>
                              {p.is_held ? <span style={badgeStyle("#FEF3C7", "#92400E")}>Held</span> : null}
                              {p.exceptions.map((x, i) => (
                                <span key={i} style={{ marginLeft: 4 }}>
                                  <Chip level={x.level}>{exceptionLabel(x.code)}</Chip>
                                </span>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </LoadState>
              </>
            ) : (
              <div style={{ ...cardStyle, padding: 24, textAlign: "center", color: "#6B7280" }}>
                {canManage ? "Nothing is calculated yet. Calculate to see what each person will be paid." : "This run has not been calculated yet."}
              </div>
            )}

            <EmployeeDrawer runId={id} employeeId={viewing} run={r} canManage={canManage} currency={currency} onHide={() => setViewing(null)} onChanged={refresh} />
            <FormDrawer
              show={rejecting}
              title="Send back for changes"
              fields={[{ key: "reason", label: "What needs to change?", type: "textarea", required: true, max: 300 }]}
              submitLabel="Send back to payroll"
              onHide={() => setRejecting(false)}
              onSave={(clean) => hrmsService.rejectPayrollRun(id, clean.reason)}
              onDone={refresh}
            />
            <FormDrawer
              show={cancelling}
              title="Cancel this run"
              fields={[{ key: "reason", label: "Reason (optional)", max: 300 }]}
              submitLabel="Yes, cancel the run"
              onHide={() => setCancelling(false)}
              onSave={(clean) => hrmsService.cancelPayrollRun(id, clean.reason)}
              onDone={refresh}
            >
              <div style={{ fontSize: 13, color: "#6B7280" }}>Nothing is paid. One-time items go back to waiting for the next run.</div>
            </FormDrawer>
            <FormDrawer
              show={fnfEditing}
              title="Settlement details"
              submitLabel="Save details"
              initial={{ leave_encashment_days: r.fnf_params?.leave_encashment_days || 0, notice_pay_days: r.fnf_params?.notice_pay_days || 0, notice_shortfall_days: r.fnf_params?.notice_shortfall_days || 0 }}
              fields={[
                { key: "leave_encashment_days", label: "Unused leave to pay (days)", type: "number", step: "0.5", min: "0" },
                { key: "notice_pay_days", label: "Notice pay (days)", type: "number", step: "0.5", min: "0" },
                { key: "notice_shortfall_days", label: "Notice recovery (days)", type: "number", step: "0.5", min: "0" },
              ]}
              onHide={() => setFnfEditing(false)}
              onSave={(clean, raw) => hrmsService.saveFnfParams(id, { leave_encashment_days: raw.leave_encashment_days || 0, notice_pay_days: raw.notice_pay_days || 0, notice_shortfall_days: raw.notice_shortfall_days || 0 })}
              onDone={refresh}
            >
              <div style={{ fontSize: 13, color: "#6B7280" }}>Calculate again after changing these.</div>
            </FormDrawer>
          </>
        ) : null}
      </LoadState>
    </div>
  );
}
